// Utility for managing allowlist with server-side storage
// Falls back to local storage if server is unavailable
import CryptoJS from 'crypto-js';

// Local storage key (fallback only)
const ALLOWLIST_STORAGE_KEY = 'codes_access_allowlist';

// Initialize with default allowed user  
const DEFAULT_ALLOWLIST = ['paul', 'marshall', 'sami', 'long', 'tatiana', 'ivan', 'marlowe', 'tony', 'robert', 'erin', 'ts', 'alexa', 'sheldon', 'jd'];

// Secret key for encryption (in production, this should be more secure)
const SECRET_KEY = 'mmuf-codes-access-2024';

// Cache for server allowlist to avoid repeated API calls
let serverAllowlistCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Import API functions (will be injected by components that use this)
let apiContext = null;

export const setAPIContext = (context) => {
  apiContext = context;
};

// Encrypt data
const encrypt = (data) => {
  try {
    const jsonStr = JSON.stringify(data);
    const encrypted = CryptoJS.AES.encrypt(jsonStr, SECRET_KEY).toString();
    return encrypted;
  } catch (error) {
    console.error('Failed to encrypt allowlist:', error);
    return null;
  }
};

// Decrypt data
const decrypt = (encryptedData) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
    const decryptedStr = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decryptedStr);
  } catch (error) {
    console.error('Failed to decrypt allowlist:', error);
    return null;
  }
};

// Get allowlist from server (with local fallback)
export const getAllowlist = async () => {
  try {
    // Check cache first
    if (serverAllowlistCache && cacheTimestamp && (Date.now() - cacheTimestamp) < CACHE_DURATION) {
      return serverAllowlistCache;
    }

    // Try to get from server
    if (apiContext && apiContext.getAllowlistFromServer) {
      try {
        const response = await apiContext.getAllowlistFromServer();
        if (response && response.allowlist && Array.isArray(response.allowlist)) {
          serverAllowlistCache = response.allowlist;
          cacheTimestamp = Date.now();
          return response.allowlist;
        }
      } catch (serverError) {
        console.warn('Failed to get allowlist from server, falling back to local:', serverError);
      }
    }

    // Fallback to local storage
    return getLocalAllowlist();
  } catch (error) {
    console.error('Error getting allowlist:', error);
    return DEFAULT_ALLOWLIST;
  }
};

// Get allowlist from local storage (fallback)
const getLocalAllowlist = () => {
  try {
    const encryptedList = localStorage.getItem(ALLOWLIST_STORAGE_KEY);
    
    if (!encryptedList) {
      return DEFAULT_ALLOWLIST;
    }
    
    const decrypted = decrypt(encryptedList);
    return decrypted || DEFAULT_ALLOWLIST;
  } catch (error) {
    console.error('Error getting local allowlist:', error);
    return DEFAULT_ALLOWLIST;
  }
};

// Save allowlist to server (with local fallback)
export const saveAllowlist = async (allowlist) => {
  try {
    // Try to save to server first
    if (apiContext && apiContext.saveAllowlistToServer) {
      try {
        await apiContext.saveAllowlistToServer(allowlist);
        // Update cache
        serverAllowlistCache = [...allowlist];
        cacheTimestamp = Date.now();
        // Also save locally as backup
        saveLocalAllowlist(allowlist);
        return true;
      } catch (serverError) {
        console.warn('Failed to save allowlist to server, saving locally:', serverError);
      }
    }

    // Fallback to local storage
    return saveLocalAllowlist(allowlist);
  } catch (error) {
    console.error('Error saving allowlist:', error);
    return false;
  }
};

// Save allowlist to local storage (fallback)
const saveLocalAllowlist = (allowlist) => {
  try {
    const encrypted = encrypt(allowlist);
    localStorage.setItem(ALLOWLIST_STORAGE_KEY, encrypted);
    return true;
  } catch (error) {
    console.error('Error saving local allowlist:', error);
    return false;
  }
};

// Check if user is in allowlist
export const isUserAllowed = async (email) => {
  if (!email) return false;
  
  // Extract alias from email (part before @)
  const alias = email.split('@')[0].toLowerCase();
  const allowlist = await getAllowlist();
  
  return allowlist.map(a => a.toLowerCase()).includes(alias);
};

// Add user to allowlist
export const addToAllowlist = async (alias) => {
  try {
    const normalizedAlias = alias.toLowerCase().replace('@', '').split('@')[0]; // Clean input
    
    // Try to add via server API first
    if (apiContext && apiContext.addUserToServerAllowlist) {
      try {
        await apiContext.addUserToServerAllowlist(normalizedAlias);
        // Clear cache to force refresh
        serverAllowlistCache = null;
        cacheTimestamp = null;
        return true;
      } catch (serverError) {
        console.warn('Failed to add user to server allowlist, falling back to local:', serverError);
      }
    }

    // Fallback to local storage method
    const allowlist = await getAllowlist();
    
    // Check if already exists (case insensitive)
    const existsInList = allowlist.map(a => a.toLowerCase()).includes(normalizedAlias);
    
    if (!existsInList) {
      allowlist.push(normalizedAlias);
      return await saveAllowlist(allowlist);
    }
    return true;
  } catch (error) {
    console.error('Error adding user to allowlist:', error);
    return false;
  }
};

// Remove user from allowlist
export const removeFromAllowlist = async (alias) => {
  try {
    const normalizedAlias = alias.toLowerCase().replace('@', '').split('@')[0]; // Clean input
    
    // Try to remove via server API first
    if (apiContext && apiContext.removeUserFromServerAllowlist) {
      try {
        await apiContext.removeUserFromServerAllowlist(normalizedAlias);
        // Clear cache to force refresh
        serverAllowlistCache = null;
        cacheTimestamp = null;
        return true;
      } catch (serverError) {
        console.warn('Failed to remove user from server allowlist, falling back to local:', serverError);
      }
    }

    // Fallback to local storage method
    const allowlist = await getAllowlist();
    const filtered = allowlist.filter(a => a.toLowerCase() !== normalizedAlias);
    
    return await saveAllowlist(filtered);
  } catch (error) {
    console.error('Error removing user from allowlist:', error);
    return false;
  }
};

// Initialize allowlist if not exists
export const initializeAllowlist = async () => {
  try {
    // Try to get from server first
    const serverAllowlist = await getAllowlist();
    if (serverAllowlist && serverAllowlist.length > 0) {
      return; // Server has data, we're good
    }
    
    // If no server data, check local storage
    const existing = localStorage.getItem(ALLOWLIST_STORAGE_KEY);
    if (!existing) {
      await saveAllowlist(DEFAULT_ALLOWLIST);
    }
  } catch (error) {
    console.error('Error initializing allowlist:', error);
    // Fallback to local storage initialization
    const existing = localStorage.getItem(ALLOWLIST_STORAGE_KEY);
    if (!existing) {
      saveLocalAllowlist(DEFAULT_ALLOWLIST);
    }
  }
};